import { useDialog } from "../components/DialogProvider";
import { ActionStatus } from "../components/ActionStatus";
import { UserPicker } from "../components/UserPicker";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createTag, listTags, tagImpact, updateTag } from "../api/tags";
import { hasGroup, useAuth } from "../auth/AuthContext";
import type { RecordingTag, TagGrant } from "../types";

export default function TagsPage() {
  const { confirm } = useDialog();
  const [tags, setTags] = useState<RecordingTag[]>([]);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const createLock = useRef(false);
  const { user } = useAuth();
  const canCreate =
    hasGroup(user, "recordings-manager") ||
    hasGroup(user, "recordings-supermanager") ||
    hasGroup(user, "recordings-admin");
  const refresh = useCallback(async () => {
    try {
      setTags(await listTags());
      setError("");
    } catch {
      setError("Не удалось загрузить метки");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return (
    <div className="page">
      <h1>Метки</h1>
      {canCreate && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!name.trim() || createLock.current) return;
            createLock.current = true;
            try {
              if (
                tags.some((t) => t.name.toLocaleLowerCase() === name.trim().toLocaleLowerCase()) &&
                !await confirm({ title: "Метка уже существует", description: "Похожая метка уже существует. Создать ещё одну?", confirmLabel: "Создать" })
              ) return;
              setCreating(true);
              await createTag(name.trim());
              setName("");
              await refresh();
            } catch {
              setError("Не удалось создать метку");
            } finally {
              createLock.current = false;
              setCreating(false);
            }
          }}
        >
          <label>
            Новая метка{" "}
            <input
              className="filter-input"
              list="existing-tag-names"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={150}
              disabled={creating || loading}
            />
          </label>
          <datalist id="existing-tag-names">
            {tags.map((t) => (
              <option key={t.id} value={t.name} />
            ))}
          </datalist>{" "}
          <button className="page-button" type="submit" disabled={creating || loading || !name.trim()}>Создать</button>
          {creating && <> <ActionStatus>Создаём метку…</ActionStatus></>}
          <p className="muted">Новая метка используется для поиска и никому не выдаёт доступ.</p>
        </form>
      )}
      {error && <p className="error">{error}</p>}
      {loading && <p><ActionStatus>Загружаем метки…</ActionStatus></p>}
      {tags.map((tag) => (
        <TagEditor
          key={tag.id}
          tag={tag}
          canEdit={
            canCreate &&
            (tag.owner === user?.profile.matrix_id ||
              hasGroup(user, "recordings-supermanager") ||
              hasGroup(user, "recordings-admin"))
          }
          onSaved={refresh}
        />
      ))}
    </div>
  );
}

function TagEditor({
  tag,
  canEdit,
  onSaved,
}: {
  tag: RecordingTag;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  const { confirm } = useDialog();
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState(tag.color);
  const [grant, setGrant] = useState(tag.grants_access);
  const [archived, setArchived] = useState(tag.archived);
  const [principals, setPrincipals] = useState<TagGrant[]>(tag.grants ?? []);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [conflict, setConflict] = useState(false);
  const baseline = useRef(tag);
  const ownUpdate = useRef<number | null>(null);
  const animationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleId = useId();
  const detailsId = useId();
  const metaId = useId();
  const dirty = name !== baseline.current.name || color !== baseline.current.color || grant !== baseline.current.grants_access || archived !== baseline.current.archived || JSON.stringify(principals) !== JSON.stringify(baseline.current.grants ?? []);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (tag.id === baseline.current.id && tag.version === baseline.current.version) return;
    if (dirtyRef.current && tag.version !== ownUpdate.current) { setConflict(true); return; }
    ownUpdate.current = null;
    baseline.current = tag;
    setName(tag.name);
    setColor(tag.color);
    setGrant(tag.grants_access);
    setArchived(tag.archived);
    setPrincipals(tag.grants ?? []);
    setConflict(false);
  }, [tag]);
  useEffect(() => () => { if (animationTimer.current) clearTimeout(animationTimer.current); }, []);
  function toggle() {
    if (saving) return;
    if (animationTimer.current) clearTimeout(animationTimer.current);
    setAnimating(true);
    setExpanded(value => !value);
    animationTimer.current = setTimeout(() => setAnimating(false), 250);
  }
  async function save() {
    setSaving(true);
    setStatus("Проверяем связанные записи…");
    setSaved(false);
    try {
      if (principals.some(recipient => recipient.type !== "user")) {
        throw new Error("Для сохранения удалите ранее добавленные группы из получателей.");
      }
      const impact = await tagImpact(tag.id);
      if (impact.version !== baseline.current.version) {
        setConflict(true);
        throw new Error("Метку изменили после открытия. Сохраните свои правки отдельно и обновите страницу.");
      }
      setStatus("");
      if (
        !await confirm({ title: "Сохранить изменения метки?", description: `Изменение затронет ${impact.recordings} активных записей. ${grant ? `Получатели: ${principals.map(recipient => recipient.principal).join(", ") || "нет"}. Они получат доступ к существующим, будущим и восстановленным записям с меткой.` : "Выдача доступа через метку выключена."} Удалённые получатели могут потерять доступ, если у них нет другого основания.`, confirmLabel: "Сохранить" })
      )
        return;
      setStatus("Сохраняем изменения метки…");
      await updateTag({
        ...tag,
        name: name.trim(),
        color,
        grants_access: grant,
        archived,
        grants: principals,
        expected_version: impact.version,
      });
      setError("");
      ownUpdate.current = impact.version + 1;
      await onSaved();
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось изменить метку");
    } finally {
      setSaving(false);
      setStatus("");
    }
  }
  return (
    <section className={`access-list tag-editor${expanded ? " is-expanded" : ""}${animating ? " is-animating" : ""}`}>
      <h3 className="tag-editor-heading">
        <button type="button" className="tag-editor-toggle" aria-labelledby={titleId} aria-describedby={metaId} aria-controls={detailsId} aria-expanded={expanded} disabled={saving} onClick={toggle}>
          <span className="tag-editor-main"><span className="tag-dot" style={tag.color ? { background: tag.color } : undefined} aria-hidden="true" /><span id={titleId} className="tag-editor-name">{tag.name}</span></span>
          <span className="tag-editor-meta" id={metaId}><span className={tag.grants_access ? "tag-editor-access" : "tag-editor-search"}>{tag.grants_access ? "С доступом" : "Для поиска"}</span>{tag.archived && <span className="tag-editor-archive">В архиве</span>}{dirty && <span className="tag-editor-dirty">Есть изменения</span>}{conflict && <span className="tag-editor-dirty">Обновите страницу</span>}</span>
          <span className="tag-editor-chevron" aria-hidden="true">⌄</span>
        </button>
      </h3>
      <div id={detailsId} className="tag-editor-content" role="region" aria-labelledby={titleId} aria-hidden={!expanded} inert={!expanded}>
      <div className="tag-editor-content-inner">
      {canEdit ? (
        <>
          <label>
            Название <input className="filter-input" disabled={saving} value={name} onChange={(e) => setName(e.target.value)} />
          </label>{" "}
          <TagColorPicker color={color} disabled={saving} onChange={(next) => { setColor(next); setSaved(false); }} />
          {color !== tag.color && <p className="tag-color-unsaved">Цвет изменён. Нажмите «Сохранить», чтобы применить.</p>}
          <p>
            <label>
              <input type="checkbox" disabled={saving} checked={grant} onChange={(e) => setGrant(e.target.checked)} />{" "}
              Выдавать доступ через метку
            </label>
          </p>
          <div className="tag-recipients-field">
            <span className="manager-label">Получатели доступа</span>
            <p className="muted panel-note">{grant ? "После сохранения пользователи получат доступ ко всем записям с меткой." : "Пользователи получат доступ, когда включена выдача доступа через метку."}</p>
            <UserPicker
              users={principals.filter(recipient => recipient.type === "user").map(recipient => recipient.principal)}
              onChange={users => setPrincipals(previous => [...previous.filter(recipient => recipient.type !== "user"), ...users.map(principal => ({ type: "user" as const, principal }))])}
              disabled={saving}
              active={expanded}
            />
            {principals.some(recipient => recipient.type === "group") && <div className="legacy-tag-groups">
              <p className="muted panel-note">Ранее добавленные группы необходимо удалить перед сохранением.</p>
              <ul className="share-chips">{principals.filter(recipient => recipient.type === "group").map(recipient => <li className="share-chip" key={recipient.principal}><span className="share-chip-text">Группа: {recipient.principal}</span><button type="button" className="share-chip-x" disabled={saving} aria-label={`Убрать группу ${recipient.principal}`} onClick={() => setPrincipals(previous => previous.filter(item => item.type !== "group" || item.principal !== recipient.principal))}>×</button></li>)}</ul>
            </div>}
          </div>
          <p>
            <label>
              <input
                type="checkbox"
                disabled={saving}
                checked={archived}
                onChange={(e) => setArchived(e.target.checked)}
              />{" "}
              В архиве
            </label>
          </p>
          <button className="page-button" disabled={saving || conflict} onClick={() => void save()}>{saving ? "Сохранение…" : "Сохранить"}</button>
          {status && <> <ActionStatus>{status}</ActionStatus></>}
          {saved && !dirty && <span role="status"> Сохранено</span>}
        </>
      ) : (
        <p>{tag.grants_access ? "Метка с доступом" : "Для поиска"}</p>
      )}
      {conflict && !error && <p className="error" role="alert">Метку изменили после открытия. Сохраните свои правки отдельно и обновите страницу.</p>}
      {error && <p className="error">{error}</p>}
      </div>
      </div>
    </section>
  );
}

const TAG_COLORS = [
  { value: "#f87171", name: "Красный" },
  { value: "#fb923c", name: "Оранжевый" },
  { value: "#fbbf24", name: "Жёлтый" },
  { value: "#a3e635", name: "Лаймовый" },
  { value: "#4ade80", name: "Зелёный" },
  { value: "#2dd4bf", name: "Бирюзовый" },
  { value: "#38bdf8", name: "Голубой" },
  { value: "#60a5fa", name: "Синий" },
  { value: "#818cf8", name: "Индиго" },
  { value: "#c084fc", name: "Фиолетовый" },
  { value: "#e879f9", name: "Пурпурный" },
  { value: "#f472b6", name: "Розовый" },
  { value: "#fb7185", name: "Коралловый" },
  { value: "#d6a77a", name: "Песочный" },
  { value: "#94a3b8", name: "Серый" },
] as const;

function TagColorPicker({ color, disabled, onChange }: { color: string; disabled: boolean; onChange: (color: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const groupName = useId();
  const current = TAG_COLORS.find(item => item.value === color.toLowerCase());
  const custom = color !== "" && !current;
  useEffect(() => {
    if (!open) return;
    ref.current?.querySelector<HTMLInputElement>('input:checked')?.focus();
    const outside = (event: PointerEvent) => { if (!ref.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") { event.stopPropagation(); setOpen(false); triggerRef.current?.focus(); } };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [open]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  function choose(next: string) { onChange(next); setOpen(false); triggerRef.current?.focus(); }
  return <div className="tag-color-picker" ref={ref}>
    <span className="tag-color-label">Цвет метки</span>
    <button ref={triggerRef} type="button" className="tag-color-trigger" disabled={disabled} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(!open)}>
      <span className={`tag-color-preview${color ? "" : " tag-color-none"}`} style={color ? { backgroundColor: color } : undefined} aria-hidden="true" />
      <span>{current?.name ?? (color ? "Другой цвет" : "Без цвета")}</span><span className="tag-color-chevron" aria-hidden="true">⌄</span>
    </button>
    {open && <div className="tag-color-popover" role="dialog" aria-label="Выбор цвета метки">
      <p>Цвет метки</p>
      <fieldset className="tag-color-grid" aria-label="Выберите цвет">
        {[{ value: "", name: "Без цвета" }, ...TAG_COLORS, ...(custom ? [{ value: color, name: "Текущий цвет" }] : [])].map(item => <label className="tag-color-option" key={item.value} title={item.name}>
          <input type="radio" name={groupName} value={item.value} checked={color.toLowerCase() === item.value.toLowerCase()} onChange={() => choose(item.value)} />
          <span className={`tag-color-swatch${item.value ? "" : " tag-color-none"}`} style={item.value ? { backgroundColor: item.value } : undefined} aria-hidden="true">{color.toLowerCase() === item.value.toLowerCase() && <span>✓</span>}</span>
          <span className="tag-color-sr">{item.name}</span>
        </label>)}
      </fieldset>
      <small>Цвет помогает различать метки и не влияет на доступ.</small>
    </div>}
  </div>;
}
